<template>
  <div
    class="greyactivitybackground"
    :class="mobile() ? 'activity-page-container-mobile' : 'activity-page-container'"
  >
    <AlertMessages
      v-if="alertValue"
      :alert-type="alertType" 
      :alert-msg="alertMsg"
      @reset="alertValue = false"
    />

    <!-- EDIT MODE -->
    <v-card
      max-width="960px"
      width="100%"
      height="100%"
      class="activity-page-height edit-mode edit-mode-page mx-auto"
    >
      <draggable
        v-if="!listHtml.length && !loadingData"
        ref="dragArea"
        class="dragArea dragAreaEmptyPage list-group mx-auto"
        :list="listHtml"
        group="blocks"
        ghost-class="ghost-block"
        @change="log"
      >
        <v-row
          no-gutters
          class="fill-height"
          align="center"
          data-testid="empty-page-content"
        >
          <v-col
            cols="12"
            class="mx-auto pa-0"
            align="center"
          >
            <div class="greytextlight--text text-h6 text-md-h5 font-weight-bold mx-auto pa-4">
              {{ $t('emptyEditActivityPageText') }}
            </div>                     
            <div ref="editPage" />
          </v-col>
        </v-row>
      </draggable>
      <!-- :draggable property is added because was having problems in some browsers -->
      <!-- https://github.com/ueberdosis/tiptap/issues/379 -->
      <draggable
        v-if="listHtml.length>0 && !loadingData"
        ref="dragArea"
        class="dragArea list-group"
        :list="listHtml"
        group="blocks"
        ghost-class="ghost-block"   
        :sort="false"
        :draggable="showBlocks"
        @change="log"
      >
        <div
          v-for="(element, index) in listHtml"
          :key="index"
          class="list-group-item"
          :class="{'first' : index == 0, 'last': index == listHtml.length - 1}"
        > 
          <v-icon
            v-if="index != 0"
            class="moveup-icon d-none"
            color="accent"
            data-testid="moveup-icon"
            dense
            @click="moveBlock(index, index-1)"
          >
            mdi-chevron-up
          </v-icon>
          <v-toolbar
            flat
            class="d-none"
            height="94px"
          >
            <v-tooltip
              :bottom="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm || $vuetify.breakpoint.md"
              :right="$vuetify.breakpoint.lg || $vuetify.breakpoint.xl"
            >
              <template v-slot:activator="{ on }">
                <v-icon
                  class="d-block pa-1"
                  data-testid="delete-icon"
                  dense
                  color="accent"
                  v-on="on"
                  @click="deleteBlock(index)"
                >
                  mdi-delete
                </v-icon>
              </template>
              <span>{{ $t('deleteBlock') }}</span>
            </v-tooltip>
            <v-tooltip
              :bottom="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm || $vuetify.breakpoint.md"
              :right="$vuetify.breakpoint.lg || $vuetify.breakpoint.xl"
            >
              <template v-slot:activator="{ on }">
                <v-icon
                  class="d-block pa-1"
                  data-testid="copy-icon"
                  dense
                  color="accent"
                  v-on="on"
                  @click="copyBlock($event, element, index)"
                >
                  mdi-content-copy
                </v-icon>
              </template>
              <span>{{ $t('duplicateBlock') }}</span>
            </v-tooltip>
            <v-tooltip
                :bottom="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm || $vuetify.breakpoint.md"
                :right="$vuetify.breakpoint.lg || $vuetify.breakpoint.xl"
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    class="d-block pa-1"
                    data-testid="settings-icon"
                    dense
                    color="accent"
                    v-on="on"
                    @click="showBlockSettings(element, index)"
                  >
                    mdi-cog
                  </v-icon>
                </template>
                <span>{{ $t('settingsBlock') }}</span>
              </v-tooltip>
            <v-spacer />
          </v-toolbar>
          <!-- eslint-disable vue/no-v-html -->
          <editor
            ref="editPage"
            :key="element.id"
            :content="element.html"
            :index="index"
            :background="element.background ? element.background : '#FFFFFF'"
            @proceedClick="setEditor"
            @updateContent="updateContent"
            @proceedMark="showHideEditorTextOptions"
            @enableEditMode="showEditMode = true, showBlocks = false"
          />
          <v-icon
            v-if="index != (listHtml.length-1)"
            class="movedown-icon d-none"
            color="accent"
            data-testid="movedown-icon"
            dense
            @click="moveBlock(index, index+1)"
          >
            mdi-chevron-down
          </v-icon> 
        </div>
      </draggable>
    </v-card>

    <v-navigation-drawer
      app
      permanent
      right
      clipped
      data-testid="navigation-drawer"
      width="300"
    >
      <template
        v-if="!loadingData"
        v-slot:prepend
      >
        <v-row class="ma-0">
          <v-col cols="5"> 
            <v-tooltip
              v-if="loadingAutosave"
              top
            >
              <template
                v-slot:activator="{ on }"
              >
                <v-icon
                  data-testid="saving-draft-icon"
                  dense
                  class="mr-2"
                  v-on="on"
                  @click="autosavePageAsDraft(false)"
                >
                  mdi-cloud-sync-outline
                </v-icon>
              </template>
              <span>{{ $t('savingTooltip') }}</span>
            </v-tooltip>
            <v-tooltip
              v-else
              top
            >
              <template
                v-slot:activator="{ on }"
              >
                <v-icon
                  data-testid="save-draft-icon"
                  dense
                  class="mr-2"
                  v-on="on"
                  @click="autosavePageAsDraft(false)"
                >
                  mdi-cloud-outline
                </v-icon>
              </template>
              <span>{{ $t('saveAsDraftTooltip') }}</span>
            </v-tooltip>
            <v-tooltip
              v-if="draftVersion"
              top
            >
              <template v-slot:activator="{ on }">
                <v-icon
                  data-testid="discard-draft-icon"
                  dense
                  v-on="on"
                  @click="
                    dialogDraftVisible=true, 
                    confirmationDialogTitle=$t('confirmationDialogTitleDiscardDraft'), 
                    confirmationDialogText=$t('confirmationDialogTextDiscardDraft'), 
                    confirmationDialogSubmitBtn=$t('discard')
                    confirmationDialogCancelBtn=$t('no')"
                >
                  mdi-backup-restore
                </v-icon>
              </template>
              <span>{{ $t('discardAllDraftChanges') }}</span>
            </v-tooltip>
            <div
              class="text-caption grey--text font-weight-regular pt-1"
              style="height:20px; line-height:1;"
            >
              {{ loadingAutosaveFinish ? $t('saveAsDraftSuccess') : '' }}
            </div>
          </v-col>
          <v-col
            cols="7"
            align="right"
          >                    
            <v-btn
              data-testid="submit-button"
              class="rounded-xxl text-none px-10"
              color="success"
              dark
              @click="
                dialogPublishVisible=true, 
                confirmationDialogTitle=$t('confirmationDialogTitlePublishPage'), 
                confirmationDialogText=$t('confirmationDialogTextPublishPage'), 
                confirmationDialogSubmitBtn=$t('publish')
                confirmationDialogCancelBtn=$t('saveAsDraft')
              "
            >
              {{ $t('publish') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">                    
            <div
              class="d-flex flex-column"
              style="align-items:center"
            >
              <v-btn
                icon
                class="mr-0 text-none"
                :class="{'accent--text': showEditMode}"
                data-testid="edit-button"
                @click="showEditMode = true, showBlocks = false, showSettings = false"
              >
                <v-icon class="mx-auto">
                  mdi-auto-fix
                </v-icon>
              </v-btn>   
              <span
                :style="{cursor: 'pointer'}"
                :class="{'accent--text': showEditMode}"
                @click="showEditMode = true, showBlocks = false, showSettings = false"
              >{{ $t('editorEditModeTitle') }}</span>
            </div>
          </v-col>
          <v-col
            cols="6"
            align="center"
          >
            <div
              class="d-flex flex-column"
              style="align-items:center"
            >
              <v-btn
                icon
                class="mr-0 text-none"
                :class="{'accent--text': showBlocks}"
                data-testid="blocks-button"
                @click="showBlocks = true, showEditMode = false, showSettings = false"
              >
                <v-icon class="mx-auto">
                  mdi-view-grid-plus-outline
                </v-icon>
              </v-btn>   
              <span
                :style="{cursor: 'pointer'}"
                :class="{'accent--text': showBlocks}"
                @click="showBlocks = true, showEditMode = false, showSettings = false"
              >{{ $t('editorBlocksModeTitle') }}</span>
            </div>
          </v-col>
        </v-row>
        <v-divider />
        <v-select
          v-if="showBlocks && !loadingData"
          v-model="selectedBlock.id"
          class="rounded-xxl select-items ma-5" 
          :items="listBlockTypes" 
          item-value="id" 
          item-text="name" 
          solo
          hide-details="true"
          append-icon="mdi-chevron-down"
          dense
          data-testid="blocks-type-select" 
          @change="changeBlockType"
        />
        <v-divider />
      </template>
      <v-card
        v-if="showBlocks && !loadingData"
        height="100%"
        elevation="0"
        class="px-2"
        data-testid="blocks-card"
      >
        <div>
          <draggable
            class="dragArea pb-5"
            :list="filteredBlocks"
            :group="{ name: 'blocks', pull: 'clone', put: listHtml }"
            :clone="dragDropBlockToPageLayout"
            :sort="false"
            @start="onStart"
            @end="onEnd"
            chosen-class="chosen-block"
          >
            <v-card
              v-for="element in filteredBlocks"
              :key="element.id"
              class="page-block"
              data-testid="block-card"
              :ripple="false"
              @click="addBlockToPageLayout(element.id)"
              elevation="4"
            >
              <v-card-subtitle data-testid="block-title-card">
                {{ $t(element.name) }}
              </v-card-subtitle>
              <v-img
                data-testid="block-img-card"
                :src="getImgUrl(element.image)"
              />
              <v-card-actions>
                <v-btn
                  data-testid="block-button-card"
                  text
                  class="mx-auto text-none text-body-2 greytext--text rounded-xxl"
                >
                  {{ $t('addBlockToPageLink') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </draggable>
        </div>
      </v-card>
      <editor-settings
        v-if="showEditMode && !loadingData"
        :key="listHtml.length"
        :editor="editor"
        :editor-extension="editorExtension"
        :editor-extension-properties="editorExtensionProperties"
        :editor-text-size="editorTextSize"
        :editor-activate-link-option="editorActivateLinkOption"
        :editor-link-properties="editorLinkProperties"
        :colors="contentColors"
        @disableChangingEditor="disableChangingEditor"
        @proceedUploading="proceedUploadingInEditor"
        @changeSelectedTextSize="changeSelectedTextSize"
        @changeRecentColors="changeRecentColors"
      />
      <editor-block-settings
        v-if="showSettings && !loadingData"
        :block="selectedContentBlock"
        type="page"
        :colors="contentColors"
        @changeBlockColor = "setBlockColor"
        :key="selectedContentBlock.id"
        @changeRecentColors="changeRecentColors"
      />
    </v-navigation-drawer>
    <ConfirmationDialog
      v-if="dialogDraftVisible"
      ref="confirmationDialogExit"
      :visible="dialogDraftVisible"
      :cd-title="confirmationDialogTitle"
      :cd-text="confirmationDialogText"
      :cd-submit-btn="confirmationDialogSubmitBtn"
      :cd-cancel-btn="confirmationDialogCancelBtn"
      cd-submit-btn-color="accent"
      data-testid="confirmation-dialog"
      @close="dialogDraftVisible=false"
      @proceedSubmit="discardDraft"
    />
    <ConfirmationDialog 
      v-if="dialogPublishVisible"
      ref="confirmationDialogPublish"
      :visible="dialogPublishVisible"
      :cd-title="confirmationDialogTitle"
      :cd-text="confirmationDialogText"
      :cd-submit-btn="confirmationDialogSubmitBtn"
      :cd-cancel-btn="confirmationDialogCancelBtn"
      cd-submit-btn-color="accent"
      data-testid="confirmation-dialog-publish"
      @input="clickOutside"
      @close="savePageAsDraft"
      @proceedSubmit="savePage"
    />
  </div>
</template>
<script>
import i18n from '../../plugins/i18n'
import { getActivity, getActivityPage, updateActivityPage, createActivityPageDraft, getActivityPageDraft, updateActivityPageDraft, discardPageDraft, changeImageInActivityPage, changeDocumentInActivityPage, updateActivity} from '../../api/activityApi'
import {eventBus} from '../../eventbus'
import draggable from "vuedraggable";
import {templateOfTitleTextBlock} from '../../components/editor_components/page/TitlePlusText.js'
import {templateOfTextBlock} from '../../components/editor_components/page/Text.js'
import {templateOfTextTwoColumnsBlock} from '../../components/editor_components/page/TextTwoColumns.js'
import {templateOfImageBlock} from '../../components/editor_components/page/Image.js'
import {templateOfImageTextRightBlock} from '../../components/editor_components/page/ImagePlusTextRight.js'
import {templateOfImageTextLeftBlock} from '../../components/editor_components/page/ImagePlusTextLeft.js'
import {templateOfImageTwoColumnsTextBelowBlock} from '../../components/editor_components/page/TwoImagesPlusTextBelow.js'
import {templateOfVideoBlock} from '../../components/editor_components/page/Video.js'
import {templateOfVideoTextRightBlock} from '../../components/editor_components/page/VideoPlusTextRight.js'
import {templateOfVideoTextLeftBlock} from '../../components/editor_components/page/VideoPlusTextLeft.js'
import {templateOfDocumentBlock} from '../../components/editor_components/page/Document.js'
import {templateOfAccordionBlock} from '../../components/editor_components/page/Accordion.js'
import {templateOfAccordionOneRowBlock} from '../../components/editor_components/page/AccordionOneRow.js'
import {templateOfImageThreeColumnsTextBelowBlock} from '../../components/editor_components/page/ThreeImagesPlusTextBelow.js'
import {templateOfImageFourColumnsTextBelowBlock} from '../../components/editor_components/page/FourImagesPlusTextBelow.js'
import {templateOfImageThreeColumns} from '../../components/editor_components/page/ThreeImages.js'
import {templateOfImageFourColumns} from '../../components/editor_components/page/FourImages.js'
import {templateOfTextThreeColumnsBlock} from '../../components/editor_components/page/TextThreeColumns.js'
import {templateOfTextFourColumnsBlock} from '../../components/editor_components/page/TextFourColumns.js'
import {templateOfAudioBlock} from '../../components/editor_components/page/Audio.js'
import {templateOfAudioBlockWithPercentage} from '../../components/editor_components/page/AudioWithPercentage.js'
import {templateOfTextTableBlock} from '../../components/editor_components/page/Table.js'
import ConfirmationDialog from '../../components/ConfirmationDialog';
import AlertMessages  from '../../components/AlertMessages'
import Editor from '../../components/Editor';
import EditorSettings from '../../components/EditorSettings';
import { repeatedMethodsMixin } from '../../mixins/repeatedMethodsMixin'
import {templateOfEmbeddedCode} from '../../components/editor_components/page/EmbedCode.js'
import EditorBlockSettings from '../../components/EditorBlockSettings';

export default {
    components: {
        draggable,
        ConfirmationDialog,
        AlertMessages,
        Editor,
        EditorSettings,
        EditorBlockSettings
    },
    mixins: [repeatedMethodsMixin],
    data(){
         return{
            pageContent: [],
            pageId: this.$route.params.pageId,
            activityId: this.$route.params.id,
            title: '',
            selectedBlock: {
                id: 'text',
            },
            listHtml:[],
            listBlockTypes:[
                {
                    id: 'text',
                    name: i18n.t('text')
                },
                {
                    id: 'image',
                    name: i18n.t('images')
                },
                {
                    id: 'video',
                    name: i18n.t('video')
                },
                {
                    id: 'document',
                    name: i18n.t('file')
                },
                {
                    id: 'accordion',
                    name: i18n.t('accordion')
                },
                {
                    id: 'audio',
                    name: i18n.t('audio')
                },
                {
                    id: 'iframe',
                    name: i18n.t('iframe')
                }
            ],
            
            listBlocks: [
                { name: 'textBlock1', image: 'titleandtext.jpg', html: templateOfTitleTextBlock, id: 1, group: 'text'},
                { name: 'textBlock2', image: 'text.jpg', html: templateOfTextBlock, id: 2, group: 'text'},
                { name: 'textBlock3', image: 'texttwocolumns.jpg', html: templateOfTextTwoColumnsBlock, id: 3, group: 'text'},
                { name: 'imageBlock1', image: 'image.jpg', html: templateOfImageBlock, id: 4, group: 'image'},
                { name: 'imageBlock2', image: 'imageandtextright.jpg', html: templateOfImageTextRightBlock, id: 5, group: 'image'},
                { name: 'imageBlock3', image: 'imageandtextleft.jpg', html: templateOfImageTextLeftBlock, id: 6, group: 'image'},
                { name: 'imageBlock4', image: 'twoimagesandtextbelow.jpg', html: templateOfImageTwoColumnsTextBelowBlock, id: 7, group: 'image'},
                { name: 'videoBlock1', image: 'video.jpg', html: templateOfVideoBlock, id: 8, group: 'video'},
                { name: 'videoBlock2', image: 'videoandtextright.jpg', html: templateOfVideoTextRightBlock, id: 9, group: 'video'},
                { name: 'videoBlock3', image: 'videoandtextleft.jpg', html: templateOfVideoTextLeftBlock, id: 10, group: 'video'},
                { name: 'documentBlock1', image: 'document.jpg', html: templateOfDocumentBlock, id: 11, group: 'document'},
                { name: 'accordionBlock1', image: 'accordion.jpg', html: templateOfAccordionBlock, id: 12, group: 'accordion'},
                { name: 'accordionBlock2', image: 'accordiononerow.jpg', html: templateOfAccordionOneRowBlock, id: 13, group: 'accordion'},
                { name: 'imageBlock5', image: 'threeimages.jpg', html: templateOfImageThreeColumns, id: 14, group: 'image'},
                { name: 'imageBlock6', image: 'threeimagesandtextbelow.jpg', html: templateOfImageThreeColumnsTextBelowBlock, id: 15, group: 'image'},
                { name: 'imageBlock7', image: 'fourimages.jpg', html: templateOfImageFourColumns, id: 16, group: 'image'},
                { name: 'imageBlock8', image: 'fourimagesandtextbelow.jpg', html: templateOfImageFourColumnsTextBelowBlock, id: 17, group: 'image'},
                { name: 'textBlock4', image: 'textthreecolumns.jpg', html: templateOfTextThreeColumnsBlock, id: 18, group: 'text'},
                { name: 'textBlock5', image: 'textfourcolumns.jpg', html: templateOfTextFourColumnsBlock, id: 19, group: 'text'},
                { name: 'audioBlock1', image: 'audiowithpercentage.jpg', html: templateOfAudioBlockWithPercentage, id: 20, group: 'audio'},
                { name: 'audioBlock2', image: 'audio.jpg', html: templateOfAudioBlock, id: 21, group: 'audio'},
                { name: 'iframeBlock1', image: 'iframe.jpg', html: templateOfEmbeddedCode, id: 22, group: 'iframe'},
                { name: 'textBlock6', image: 'table.jpg', html: templateOfTextTableBlock, id: 23, group: 'text'}
            ],
            blockType: 'text',
            showBlocks: true,
            showEditMode: false,
            editorHtml: [],
            editorHtmlContent: '',
            loadingData: false,
            alertMsg: '',
            alertType: '',
            alertValue:false,
            draftVersion: false,
            draftVersionId: 0,
            dialogDraftVisible: false,
            dialogPublishVisible: false,
            confirmationDialogTitle: '',
            confirmationDialogText: '',
            confirmationDialogCancelBtn: '',
            confirmationDialogSubmitBtn: '',
            editor: null,
            editorExtension: '',
            editorExtensionProperties: {},
            editorTextSize: 'p-normal',
            editorActivateLinkOption: false,
            spaceId: window.$cookies.get('space_id'),
            disableChangingEditorVal: false,
            editorLinkProperties: {},
            preventRedirect: false,
            loadingAutosave: false,
            loadingAutosaveFinish: false,
            occupyInterval:null,
            role:'',
            showSettings: false,
            selectedContentBlock: {},
            selectedContentBlockIndex: '',
            contentColors: []
        }
    },
    computed:{
        filteredBlocks() {
            return this.listBlocks.filter((item) =>{
                return item.group.toLowerCase().match(this.blockType)
            });
        },  
        productPlan() {
        return this.$store.state.productPlan;
      }, 
    },
    watch:{
        showBlocks(val){
            if(val){
                this.disableEditors()
            }
        },
        showEditMode(val){
            if(val){
                this.enableEditors()
            }
        },
        preventRedirect(val){
          eventBus.$emit('showDialogOnExit', val)
        }
    },
    mounted() { 
      if(this.productPlan.attributes && this.productPlan.attributes.name != 'Free'){
        this.occupyEditor(); 
        this.occupyInterval = setInterval(() => {        
          this.occupyEditor(); 
        }, 60000)  
      }
    },
    async created(){

      //if someone else is editing
        if(this.$store.state.list.find(user => user.entity_id == this.pageId )){
            this.$router.push({name: 'ViewActivityPage', params: {id: this.activityId, pageId: this.pageId, preventEditing: true}})
        }

        eventBus.$on('savePageAsDraft',()=>{
            this.savePageAsDraft()
        })

        eventBus.$on('allowRedirect',()=>{
            this.preventRedirect = false
        })

        this.loadingData = true
        await this.getActivity()
        if (this.role == 'participant' || this.role == 'guest') {
          this.$router.push({name: 'PageAccessDenied'})
        }
        await this.getActivityPage()

        //create element just for manipulating file.htmls
        if(this.pageContent.length > 0){
            var element = document.createElement('div');
            element.insertAdjacentHTML('beforeend', this.pageContent.join('<section-separator></section-separator>'));
            
            if(element.querySelectorAll("[pathAudio]").length > 0){
                await this.changeTemporaryURLsAudio(element.querySelectorAll("[pathAudio]"), element)
            }
            if(element.querySelectorAll("[path]").length > 0){
                await this.changeTemporaryURLs(element.querySelectorAll("[path]"), element)
            }
            if(element.querySelectorAll("[pathDoc]").length > 0){
                await this.changeTemporaryURLsDoc(element.querySelectorAll("[pathDoc]"), element)
            }
            
        }

        this.listHtml.forEach((value, index) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(this.listHtml[index].html, 'text/html');
          const div = doc.querySelector('div');
          const styleAttribute = div.getAttribute('style');
          var colorValue = '#FFFFFF'
          if(styleAttribute != null){
            colorValue = styleAttribute.match(/background-color:\s*(.*?);/) ? styleAttribute.match(/background-color:\s*(.*?);/)[1] : '#FFFFFF';
          }
          this.listHtml[index].background = colorValue
        })
        
        this.loadingData = false

        //start autosaving if some change is done on the page
        this.$watch('listHtml', function () {
            if(!this.preventRedirect){
                this.preventRedirect = true
                //autosave draft version every 15s
                this.autosaveInterval = setInterval(() => this.autosavePageAsDraft(true), 15000);
            }

        }, { deep: true })       
    },
    beforeMount() {
        window.addEventListener("beforeunload", this.preventNav)
    },
    beforeDestroy() {
        window.removeEventListener("beforeunload", this.preventNav);
        eventBus.$off('savePageAsDraft')
        eventBus.$off('allowRedirect')
        clearInterval(this.autosaveInterval)
        clearInterval(this.occupyInterval)
    },
    methods:{
        occupyEditor(){
          var payload = {'key1': this.pageId, 'key2': this.activityId,'key3': 'activity_page' }        
            this.$store.dispatch("occupyEditor", payload).catch(() => {
                    this.alertValue = true
                    this.alertType = 'error'
                    this.alertMsg =  i18n.t('serverError')                  
              })     
        },
        releaseEditor(){
          var payload = {'key1': this.pageId, 'key2': this.activityId,'key3': 'activity_page' }
          this.$store.dispatch("releaseEditor", payload)
          .catch(() => {
              this.alertValue = true
              this.alertType = 'error'
              this.alertMsg =  i18n.t('serverError') 
            
        })
        },
        async getActivity(){
            try {
                const response = await getActivity(this.activityId)
                if(response.status == 200) {
                    this.role = response.data.data.attributes.role
                    this.contentColors = response.data.data.attributes.content_colors == null ? [] : response.data.data.attributes.content_colors
                } 
            } catch (e) {
                if(e.response){
                    if(e.response.status == 404) {
                        if(this.isAuth){
                            this.$router.push({name: 'PageNotFound'})
                        }else{
                            this.$router.push({name: 'CustomLogin', query: {referer: this.$route.fullPath}})
                        }
                    }else if(e.response.status == 403){
                        this.$router.push({name: 'PageAccessDenied'})
                    }else {
                        this.alertValue = true
                        this.alertType = 'error'
                        this.alertMsg =  i18n.t('serverError')
                    }
                }
            }
        },
        async changeTemporaryURLs(nodes, element){
            var listNodes = []
            this.listHtml= []
            for(var i = 0; i < nodes.length; i++){
                if(listNodes.indexOf(nodes[i].attributes.path.nodeValue) === -1) {
                  listNodes.push(nodes[i].attributes.path.nodeValue);
                }
            }
            try {
                let data = {
                    data: {
                        type: "activity-pages", 
                        id: this.pageId,
                        attributes: {
                            paths: listNodes,
                            space_id: this.spaceId
                        }
                    }
                };
                const response = await changeImageInActivityPage(this.pageId, data)
                Object.keys(response.data.meta.files).forEach(function(key) {
                    //find and change in htmlSKB
                    element.querySelectorAll('[path="' + key +'"]').forEach(function(item){
                        item.attributes.src.nodeValue=response.data.meta.files[key]
                    })
                });
                this.pageContent = element.innerHTML.split('<section-separator></section-separator>')
                this.pageContent.forEach((value, index) => {
                    this.listHtml.push({ html: this.pageContent[index], id: index });
                });
            } catch (e) {
                this.alertValue = true
                this.alertType = 'error';
                this.alertMsg =  i18n.t('serverError');
            }       
        },

        async changeTemporaryURLsDoc(nodes, element){
            var listNodes = []
            this.listHtml= []
            for(var i = 0; i < nodes.length; i++){
                if(listNodes.indexOf(nodes[i].attributes.pathDoc.nodeValue) === -1) {
                    listNodes.push(nodes[i].attributes.pathDoc.nodeValue);
                }
            }
            try {
                let data = {
                    data: {
                        type: "activity-pages", 
                        id: this.pageId,
                        attributes: {
                            paths: listNodes.filter((x, i, a) => a.indexOf(x) == i),
                            space_id: this.spaceId
                        }
                    }
                };
                const response = await changeDocumentInActivityPage(this.pageId, data)
                Object.keys(response.data.meta.files).forEach(function(key) {
                    //find and change in html
                    element.querySelectorAll('[pathDoc="' + key +'"]').forEach(function(item){
                        item.attributes.href.nodeValue=response.data.meta.files[key].url
                    })
                });
                this.pageContent = element.innerHTML.split('<section-separator></section-separator>')
                this.pageContent.forEach((value, index) => {
                    this.listHtml.push({ html: this.pageContent[index], id: index });
                });
            } catch (e) {
                this.alertValue = true
                this.alertType = 'error';
                this.alertMsg =  i18n.t('serverError');
            }       
        },

        async changeTemporaryURLsAudio(nodes, element){
            var listNodes = []
            this.listHtml= []
            for(var i = 0; i < nodes.length; i++){
                if(listNodes.indexOf(nodes[i].attributes.pathAudio.nodeValue) === -1) {
                    listNodes.push(nodes[i].attributes.pathAudio.nodeValue);
                }
            }
            try {
                let data = {
                    data: {
                        type: "activity-pages", 
                        id: this.pageId,
                        attributes: {
                            paths: listNodes.filter((x, i, a) => a.indexOf(x) == i),
                            space_id: this.spaceId
                        }
                    }
                };
                const response = await changeDocumentInActivityPage(this.pageId, data)
                Object.keys(response.data.meta.files).forEach(function(key) {
                    //find and change in html
                    element.querySelectorAll('[pathAudio="' + key +'"]').forEach(function(item){
                        item.attributes.file.nodeValue=response.data.meta.files[key].url
                    })
                });
                this.pageContent = element.innerHTML.split('<section-separator></section-separator>')
                this.pageContent.forEach((value, index) => {
                    this.listHtml.push({ html: this.pageContent[index], id: index });
                });
            } catch (e) {
                this.alertValue = true
                this.alertType = 'error';
                this.alertMsg =  i18n.t('serverError');
            }       
        },

        setEditor(editorSelected){
            this.showSettings = false
            if(editorSelected == null){
                this.editor = null
            }else {
                if(!this.disableChangingEditorVal){
                    this.editor = editorSelected
                    this.$nextTick(async () => {
                        if(editorSelected.view.state.selection.node){
                            this.editorExtension = editorSelected.view.state.selection.node.type.name
                            this.editorExtensionProperties = editorSelected.view.state.selection.node
                            if((this.editorExtension =='custom-image' || this.editorExtension =='custom-image-banner') && this.editorExtensionProperties.attrs.path != null){
                                let data = {
                                    data: {
                                        type: "activity-pages", 
                                        id: this.pageId,
                                        attributes: {
                                            paths: [this.editorExtensionProperties.attrs.path],
                                            space_id: this.spaceId
                                        }
                                    }
                                };
                                const response = await changeImageInActivityPage(this.pageId, data)
                                this.editorExtensionProperties.attrs.src = response.data.meta.files[this.editorExtensionProperties.attrs.path]
                            }
                        }else{
                            if(this.editor.isActive('details')){
                                if(this.editor.isActive('detailsSummary')){
                                    this.editorExtension = 'accordion-summary'
                                }else {
                                    this.editorExtension = 'accordion-content'
                                }
                            }
                            else if(this.editor.contentComponent.$el.classList.contains('document')){
                                this.editorExtension = 'document'
                                this.editor.setOptions({editable: false});

                            }
                            /*else if(this.editor.isActive('table')){
                                this.editorExtension = 'table'
                            }*/
                            else {
                                this.editorExtension = 'text'
                                if(this.editor.isActive('custom-link')){
                                    this.editorLinkProperties = this.editor.getAttributes('custom-link')
                                }else if(this.editor.isActive('heading')){
                                    this.editorTextSize = 'h'+this.editor.getAttributes('heading').level
                                }else{
                                    if(this.editor.getAttributes('paragraph').class){
                                        if(this.editor.getAttributes('paragraph').class.includes('small')){
                                            this.editorTextSize = 'p-small'
                                        }else {
                                            this.editorTextSize = 'p-normal'
                                        }
                                    }                                
                                }
                            }
                        }
                    })
                }
            }
        },
        changeSelectedTextSize(val){
            this.editorTextSize = val
        },
        async changeRecentColors(val){
          let data = {
            data: {
              type: "activities",
              id: this.activityId,
              attributes: {
                content_colors: val
              },
              relationships: {
                space: {
                  data: {
                    type: "spaces",
                    id: this.spaceId
                  }
                },
              }
            }
          }
          const response = await updateActivity(this.activityId, data)
          if (response.status == 200) {
            this.contentColors = val
          }
        },
        updateContent(index, content){
          if(content != ''){
              this.listHtml[index]['html'] = content
          }
        },
        showHideEditorTextOptions(val){
            this.editorActivateLinkOption = val
        },
        async getActivityPage(){
            try {
                const response = await getActivityPage(this.pageId)
                if(response.status == 200) {
                    this.title = response.data.data.attributes.title
                    if(response.data.data.relationships.drafts.data.length > 0){
                        this.draftVersion = true
                        this.draftVersionId = response.data.data.relationships.drafts.data[0].id
                        const response_draft = await getActivityPageDraft(this.draftVersionId)
                        if(response_draft.status == 200) {
                            if(response_draft.data.data.attributes.content != null){
                                this.pageContent = response_draft.data.data.attributes.content.split('<section-separator></section-separator>')
                            }
                        }
                    }else {
                        if(response.data.data.attributes.content != null){
                            this.pageContent = response.data.data.attributes.content.split('<section-separator></section-separator>')
                        }
                    }
                    if(this.pageContent.length > 0){
                        this.showEditMode= true
                        this.showBlocks= false
                        this.pageContent.forEach((value, index) => {
                          this.listHtml.push({ html: this.pageContent[index], id: index });
                        });
                    }                       
                }            
            } catch (e) {
                if(e.response){
                   if(e.response.status == 404) {
                     this.$router.push({name: 'PageNotFound'})
                    }else{
                        this.alertType = 'error';
                        this.alertMsg= i18n.t('dataNotRetrievedError')
                    } 
                }         
            }

        },
        dragDropBlockToPageLayout(evt) {
            return {
                id: Date.now(),
                name: evt.name,
                html: evt.html
            };
        },
        onEnd(){
            // if(this.showBlocks){
            //     this.disableEditors()
            // }
        },
        onStart(){
            this.$refs.dragArea._sortable.options.draggable = ">*"
        },
        addBlockToPageLayout: async function(id) {
            var listItem = this.listBlocks.find(item => item.id === id)
            await this.listHtml.push({ name: listItem.name, html: listItem.html, id: Date.now() });
            this.$nextTick(() => {
                window.scrollTo(0, document.body.scrollHeight || document.documentElement.scrollHeight);
            })
            // if(this.showBlocks){
            //     this.disableEditors()
            // }
        },
        getImgUrl(element) {
            var images = require.context('../../assets/editor_images', false, /\.jpg$/)
            return images('./' + element)
            
            
        },
        changeBlockType(type){
            this.blockType = type
        },
        log: function() {
            //window.console.log(evt)
        },
        moveBlock(from, to) {
            this.listHtml.splice(to, 0, this.listHtml.splice(from, 1)[0])
            // if(this.showBlocks){
            //     this.disableEditors()
            // }
        },
        deleteBlock(element){
            this.showSettings = false
            this.listHtml.splice(element, 1);
            // if(this.showBlocks){
            //     this.disableEditors()
            // }
            this.editorExtension = ''
            this.editorExtensionProperties = {}
        },

        showBlockSettings(element, index){
          this.editor = null
          this.editorExtension = ''
          this.editorExtensionProperties = {}
          this.selectedContentBlock = element
          this.selectedContentBlockIndex = index
          this.showBlocks = false
          this.showEditMode = true
          this.showSettings = true
        },

        setBlockColor(value){
          this.listHtml[this.selectedContentBlockIndex].background = value

          //because if not onUpdate is called after saving html color is not saved
          const parser = new DOMParser();
          const doc = parser.parseFromString(this.listHtml[this.selectedContentBlockIndex].html, 'text/html');
          const div = doc.querySelector('div');
          div.setAttribute('style', `background-color: ${value};`);
          this.listHtml[this.selectedContentBlockIndex].html = div.outerHTML;
        },
        async savePage(){
            this.editorHtml = []
            this.editorHtmlContent = ''
            this.preventRedirect = false
            if(this.listHtml.length > 0){
              this.listHtml.forEach((value, index) => {
                this.editorHtml.push(this.listHtml[index].html)
              })
              this.editorHtmlContent = this.editorHtml.join('<section-separator></section-separator>').replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, '').replace(/open=""/g, '')

            }else{
              this.editorHtmlContent = ''
            }
            //now send to server
            try {
                let data = {
                        data: {
                            type: "activity-pages",
                            id: this.pageId, 
                            attributes: {
                                content: this.editorHtmlContent,
                                published: true
                            },
                            relationships: {
                                activity: {
                                    data: {
                                        type: "activities",
                                        id: this.$route.params.id
                                    }
                                }
                            }
                        }
                    };
                    const response = await updateActivityPage(this.pageId, data)
                    if(response.status == 200) {
                      if(this.productPlan.attributes && this.productPlan.attributes.name != 'Free'){
                        this.releaseEditor();
                      }
                      this.$router.push({name: 'ViewActivityPage', params: {id: this.$route.params.id, pageId: this.pageId, successfullyUpdatedActivityPage: true}})
                       
                    }
                } catch (e) {
                    if(e.response){
                        if (e.response.status == 403){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].detail == 'This action is unauthorized.'){
                                    this.$router.push({name: 'PageAccessDenied'})
                                }
                                else {
                                    this.alertValue = true
                                    this.alertType = 'error'
                                    this.alertMsg =  i18n.t('serverError')
                                }
                            }
                        }else if(e.response.status == 404) {
                         this.$router.push({name: 'PageNotFound'})
                        }else{
                            this.alertValue = true
                            this.alertType = 'error'
                            this.alertMsg =  i18n.t('serverError') 
                        }           
                    }
                }
           
        },
        copyBlock(e, element, index){
            this.showSettings = false
            this.listHtml.splice(index+1, 0, {name: element.name, html: this.listHtml[index].html, id: Date.now(), background: element.background });
        },
        async savePageAsDraft(){
            this.editorHtml = []
            this.editorHtmlContent = ''
            this.preventRedirect = false
            if(this.listHtml.length > 0){
              this.listHtml.forEach((value, index) => {
                this.editorHtml.push(this.listHtml[index].html)
              })
              this.editorHtmlContent = this.editorHtml.join('<section-separator></section-separator>').replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, '').replace(/open=""/g, '')

            }else{
              this.editorHtmlContent = ''
            }
            this.editorHtmlContent.replace(/ open=" "/g, '')
            try {
                if(this.draftVersion){
                    let data = {
                        data: {
                            type: "drafts",
                            id: this.draftVersionId,
                            attributes: {
                                content: this.editorHtmlContent
                            },
                            relationships: {
                                "activity-page": {
                                    data: {
                                        type: "activity-pages",
                                        id: this.pageId
                                    }
                                }
                            }
                        }
                    };
                    const response = await updateActivityPageDraft(this.draftVersionId, data)
                    if(response.status == 200) {
                      if(this.productPlan.attributes && this.productPlan.attributes.name != 'Free'){
                        this.releaseEditor()
                      }
                      this.$router.push({name: 'ViewActivityPage', params: {id: this.$route.params.id, pageId: this.pageId, successfullyUpdatedActivityPageDraft: true}})
                    }
                }else {
                    let data = {
                        data: {
                            type: "drafts",
                            attributes: {
                                content: this.editorHtmlContent
                            },
                            relationships: {
                                "activity-page": {
                                    data: {
                                        type: "activity-pages",
                                        id: this.pageId
                                    }
                                }
                            }
                        }
                    };
                    const response = await createActivityPageDraft(data)
                    if(response.status == 201) {
                      if(this.productPlan.attributes && this.productPlan.attributes.name != 'Free'){
                        this.releaseEditor();
                      }
                      this.$router.push({name: 'ViewActivityPage', params: {id: this.$route.params.id, pageId: this.pageId, successfullyUpdatedActivityPageDraft: true}})

                    }
                }
            } catch (e) {
                if(e.response){
                    if (e.response.status == 403){
                        for(let i = 0; i < e.response.data.errors.length; i++){
                            if(e.response.data.errors[i].detail == 'This action is unauthorized.'){                              
                                this.$router.push({name: 'PageAccessDenied'})
                            }
                            else {
                                this.alertValue = true
                                this.alertType = 'error'
                                this.alertMsg =  i18n.t('serverError')
                            }
                        }
                    } else{
                        this.alertValue = true
                        this.alertType = 'error'
                        this.alertMsg =  i18n.t('serverError') 
                    }           
                }
            }
        },
        async autosavePageAsDraft(autosave){
            this.editorHtml = []
            this.editorHtmlContent = ''
            this.loadingAutosave = true
            this.loadingAutosaveFinish = false
            if(this.listHtml.length > 0){
              this.listHtml.forEach((value, index) => {
                this.editorHtml.push(this.listHtml[index].html)
              })
              this.editorHtmlContent = this.editorHtml.join('<section-separator></section-separator>').replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, '').replace(/open=""/g, '')

            }else{
              this.editorHtmlContent = ''
            }
            this.editorHtmlContent.replace(/ open=" "/g, '')
            if(this.draftVersion){
                let data = {
                    data: {
                        type: "drafts",
                        id: this.draftVersionId,
                        attributes: {
                            content: this.editorHtmlContent
                        },
                        relationships: {
                            "activity-page": {
                                data: {
                                    type: "activity-pages",
                                    id: this.pageId
                                }
                            }
                        }
                    }
                };
                await updateActivityPageDraft(this.draftVersionId, data)
            }else {
                let data = {
                    data: {
                        type: "drafts",
                        attributes: {
                            content: this.editorHtmlContent
                        },
                        relationships: {
                            "activity-page": {
                                data: {
                                    type: "activity-pages",
                                    id: this.pageId
                                }
                            }
                        }
                    }
                };
                const response = await createActivityPageDraft(data)
                if(response){
                  if(response.status == 201) {
                    this.draftVersion = true
                    this.draftVersionId = response.data.data.id
                  }
                }
            }
            if(autosave){
                this.loadingAutosave = false
            }else{
                this.loadingAutosave = false
                this.loadingAutosaveFinish = true
                setTimeout(() => {this.loadingAutosaveFinish = false}, 2000);
            }
        },
        async discardDraft(){
            this.loadingData = true
            try {
                const response = await discardPageDraft(this.draftVersionId)
                if(response.status){
                    if(response.status == 204) {
                        this.dialogDraftVisible = false
                        this.listHtml= []
                        this.draftVersion = false
                        await this.getActivityPage()
                    }
                }            
            } catch (e) {
                if(e.response){
                    if(e.response.status == 403){
                        this.$router.push({name: 'PageAccessDenied'})
                    } else {
                        this.alertValue = true
                        this.alertType = 'error'
                        this.alertMsg =  i18n.t('serverError')
                    }
                }            
            }

            this.editorExtension = ''
            this.editorExtensionProperties = {}
            
            this.loadingData = false
        },
        proceedUploadingInEditor(alertMsg){
            this.alertValue = true
            this.alertType = 'error';
            this.alertMsg = alertMsg
        },
        disableChangingEditor(val){
            this.disableChangingEditorVal = val
        },
        clickOutside(){
            this.dialogPublishVisible=false
        },
        preventNav(event) {
            if(this.preventRedirect){
                event.preventDefault()
                event.returnValue = ""
            }
        },
        disableEditors(){
            if(Array.isArray(this.$refs.editPage)){
                this.$refs.editPage.forEach((value, index) => {
                    if(this.$refs.editPage[index].editorOneColumn.isEditable){
                        this.$refs.editPage[index].editorOneColumn.setEditable(false)
                    }
                    if(this.$refs.editPage[index].editorColumnsFirst.isEditable){
                        this.$refs.editPage[index].editorColumnsFirst.setEditable(false)
                    }
                    if(this.$refs.editPage[index].editorColumnsSecond.isEditable){
                        this.$refs.editPage[index].editorColumnsSecond.setEditable(false)
                    }
                    if(this.$refs.editPage[index].editorColumnsThird.isEditable){
                        this.$refs.editPage[index].editorColumnsThird.setEditable(false)
                    }
                    if(this.$refs.editPage[index].editorColumnsFourth.isEditable){
                        this.$refs.editPage[index].editorColumnsFourth.setEditable(false)
                    }                      
                });
            }
        },
        enableEditors(){
            if(Array.isArray(this.$refs.editPage)){
                this.$refs.editPage.forEach((value, index) => {
                    if(!this.$refs.editPage[index].editorOneColumn.isEditable){
                        this.$refs.editPage[index].editorOneColumn.setEditable(true)
                    }
                    if(!this.$refs.editPage[index].editorColumnsFirst.isEditable){
                        this.$refs.editPage[index].editorColumnsFirst.setEditable(true)
                    }
                    if(!this.$refs.editPage[index].editorColumnsSecond.isEditable){
                        this.$refs.editPage[index].editorColumnsSecond.setEditable(true)
                    }
                    if(!this.$refs.editPage[index].editorColumnsThird.isEditable){
                        this.$refs.editPage[index].editorColumnsThird.setEditable(true)
                    }
                    if(!this.$refs.editPage[index].editorColumnsFourth.isEditable){
                        this.$refs.editPage[index].editorColumnsFourth.setEditable(true)
                    }                   
                });
            }
        },
    },
    beforeRouteLeave (to, from , next) {
        if(this.preventRedirect){
            const answer = window.confirm('Do you really want to leave? Changes you made may not be saved.')
            if (answer) {
               if(this.productPlan.attributes && this.productPlan.attributes.name != 'Free'){
                  this.releaseEditor();
               }
              next()
            } else {
              next(false)
            }
        }else {
            next()
        }
    },
}
        
</script>
<style lang="scss">
.ProseMirror {
  summary::marker {
    content: "" !important;
  }
  .is-empty::before {
    content: attr(data-placeholder);
    color: var(--v-greytextlight-base);
    pointer-events: none;
    height: 0;
  }
  .details-accordion {
    display: flex;
    margin: 5px 0;
    border: 1px solid var(--v-greylightBorder-base);
    border-radius: 12px;
    padding: 8px;
    overflow-x: auto;

    > button {
      display: flex;
      cursor: pointer;
      background: transparent;
      border: none;
      padding: 0;

      &::before {
        font-family: "Material Icons";
        content: "\e5cc";
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    &.is-open > button::before {
      font-family: "Material Icons";
      content: "\e5cf";
    }
    > div {
      flex: 1 1 auto;
    }
    :last-child {
      margin-bottom: 0;
    }
  }
  button:focus{
    outline: none;
  }
}
.activity-page-container.view-mode, .activity-page-container-mobile.view-mode {
  .details-accordion {
    display: flex;
    flex-direction: column;
    margin-bottom: 5px;
    border: 1px solid var(--v-greylightBorder-base);
    border-radius: 12px;
    padding: 8px;
    cursor: pointer;
    overflow-x: auto;

     > div {
      flex: 1 1 auto;
    }
    :last-child {
      margin-bottom: 0;
    }
    summary::-webkit-details-marker {display: none; }
    summary::marker {
      content: "" !important;
    }
    summary::before { 
      font-family: "Material Icons";
      content: "\e5cc"; 
    }
    table.editor-table {
      width: 98%;
    }
  }
  .details-accordion[open]{
    summary::-webkit-details-marker {display: none; }
    summary::before { 
      font-family: "Material Icons";
      content: "\e5cf"; 
    }
  }
  .content-accordion {
    padding-left: 18px;
  }
  summary:focus{
    outline: none;
  } 
}

.ProseMirror {
  table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 0;
    overflow: hidden;

    td,
    th {
      min-width: 1em;
      border: 2px solid #ced4da;
      padding: 3px 5px;
      vertical-align: top;
      box-sizing: border-box;
      position: relative;
      background-clip: padding-box;

      > * {
        margin-bottom: 0;
      }
    }

    th {
      font-weight: normal;
      text-align: left;
      background-color: #f1f3f5;
    }

    .selectedCell:after {
      z-index: 2;
      position: absolute;
      content: "";
      left: 0; right: 0; top: 0; bottom: 0;
      background: rgba(200, 200, 255, 0.4);
      pointer-events: none;
    }

    .column-resize-handle {
      position: absolute;
      right: -2px;
      top: 0;
      bottom: -2px;
      width: 4px;
      background-color: #adf;
      pointer-events: none;
    }

    p {
      margin: 0;
    }
  }
}

.activity-page-container.view-mode, .activity-page-container-mobile.view-mode{
  table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 0;
    overflow: hidden;

    td,
    th {
      min-width: 1em;
      border: 2px solid #ced4da;
      padding: 3px 5px;
      vertical-align: top;
      box-sizing: border-box;
      background-clip: padding-box;

      > * {
        margin-bottom: 0;
      }
    }

    th {
      font-weight: normal;
      text-align: left;
      background-color: #f1f3f5;
    }
    p {
      margin: 0;
    }
  }
}

.tableWrapper, .editor-table {
  overflow-x: auto;
}

.resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}
</style>